import { createSelector } from '@reduxjs/toolkit';

import { surveyDesignerSlice } from './survey-designer-slice';
import type { State } from './survey-designer-store';

export const selectSlice = (state: State) => state[surveyDesignerSlice.name];

export const selectActiveBlockEditorTab = (state: State) => {
  const slice = selectSlice(state);
  return slice.activeBlockEditorTab;
};

export const selectActiveSurveyConfigBlock = (state: State) => {
  const slice = selectSlice(state);
  return slice.activeSurveyConfigBlock;
};

export const selectActiveSurveyConfigBlockGroup = (state: State) => {
  const slice = selectSlice(state);
  return slice.activeSurveyConfigBlockGroup;
};

export const selectSurveyBundlesDisplay = (state: State, surveyConfigId: string) => {
  const slice = selectSlice(state);
  return slice.surveyBundlesDisplay[surveyConfigId];
};

export const selectSurveyConfigBlocksDisplay = (state: State, surveyConfigId: string) => {
  const slice = selectSlice(state);
  return slice.surveyConfigBlocksDisplay[surveyConfigId];
};

export const selectOrderedSurveyConfigBlocks = (state: State) => {
  const slice = selectSlice(state);
  return slice.orderedSurveyConfigBlocks;
};

export const selectInputFocusElement = (state: State) => {
  const slice = selectSlice(state);
  return slice.inputFocus;
};

export const selectIsSurveyConfigValid = (state: State) => {
  const slice = selectSlice(state);
  return slice.isSurveyConfigValid;
};

export const selectIsSkipLogicLoading = (state: State) => {
  const slice = selectSlice(state);
  return slice.isSkipLogicLoading;
};

export const selectInvalidSurveyConfigBlocks = (state: State) => {
  const slice = selectSlice(state);
  return slice.invalidSurveyConfigBlocks;
};

export const selectIsProjectLocked = (state: State) => {
  const slice = selectSlice(state);
  return slice.isProjectLocked;
};

export const selectProjectId = (state: State) => {
  const slice = selectSlice(state);
  return slice.projectId;
};

export const selectScrollToSurveyConfigBlockId = (state: State) => {
  const slice = selectSlice(state);
  return slice.scrollToSurveyConfigBlockId;
};

export const selectSolutionId = (state: State) => {
  const slice = selectSlice(state);
  return slice.solutionId;
};

export const selectValidationError = (state: State) => {
  const slice = selectSlice(state);
  return slice.validationError;
};

export const selectIsDirectBlueprintReset = (state: State): boolean => {
  const slice = selectSlice(state);
  return slice.isDirectBlueprintReset;
};

export const selectSurveyConfigBlocksSelection = (state: State, surveyConfigId: string) => {
  const slice = selectSlice(state);
  return slice.surveyConfigBlocksSelection[surveyConfigId];
};

export const selectAllSurveyConfigBlocksSelection = (state: State) => {
  const slice = selectSlice(state);
  return slice.surveyConfigBlocksSelection;
};

export const selectSurveyConfigBlockGroupsSelection = (state: State, surveyConfigId: string) => {
  const slice = selectSlice(state);
  return slice.surveyConfigBlockGroupsSelection[surveyConfigId];
};

const EMPTY_ARRAY: string[] = [];

export const selectDeletingSurveyConfigBlockGroupIds = createSelector(
  [
    (state: State) => selectSlice(state).deletingSurveyConfigBlockGroupIds,
    (state: State) => selectSlice(state).orderedSurveyConfigBlocks,
    (_state: State, surveyConfigId: string) => surveyConfigId,
  ],
  (deletingGroupIds, orderedBlocks, surveyConfigId) => {
    const ids = deletingGroupIds[surveyConfigId] ?? EMPTY_ARRAY;
    if (ids.length === 0) {
      return EMPTY_ARRAY;
    }

    // Maintain deletion group ids only as long as blocks referencing them still exist
    const existingBlockGroupIds = new Set(
      orderedBlocks.map((b) => b.surveyConfigBlockGroupId).filter(Boolean),
    );
    return ids.filter((id) => existingBlockGroupIds.has(id));
  },
);

export const selectAllSurveyConfigBlockGroupsSelection = (state: State) => {
  const slice = selectSlice(state);
  return slice.surveyConfigBlockGroupsSelection;
};

export const selectIsBulkActionsMenuOpen = (state: State) => {
  const slice = selectSlice(state);
  return slice.isBulkActionsMenuOpen;
};
